// One date format for the whole product: dd/mm/yyyy, and dd/mm/yyyy HH:mm when a
// time matters.
//
// This replaces fourteen separate fmtDate helpers that between them rendered the
// same instant four different ways. The worst offenders called
// `toLocaleDateString()` with no locale at all, so the output followed the
// VIEWER'S machine — the joined date on the team page read "8/19/2026" in the US,
// "19/8/2026" in India and "19.8.2026" in Germany. Two testers on differently
// configured laptops could file two different bugs about one screen.
//
// Built from the date parts by hand rather than via toLocaleDateString("en-GB"):
// a locale still decides separators and padding, and the point here is an output
// that cannot vary. Dates render in the viewer's own timezone, which is what a
// person means by "the day this happened".

const pad = (n) => String(n).padStart(2, "0");

// A bare "YYYY-MM-DD" is parsed by JS as UTC MIDNIGHT, so anywhere behind UTC it
// renders as the previous day — a subscription period ending "2026-08-31" showed
// as 30/08 in New York. Read those straight off the string; there is no time to
// convert, and no timezone can shift a day that was never a moment.
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

function parse(value) {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** dd/mm/yyyy — e.g. 19/08/2026. `fallback` is returned for a missing or unparseable value. */
export function formatDate(value, fallback = "—") {
  if (typeof value === "string") {
    const dateOnly = DATE_ONLY.exec(value.trim());
    if (dateOnly) return `${dateOnly[3]}/${dateOnly[2]}/${dateOnly[1]}`;
  }
  const d = parse(value);
  if (!d) return fallback;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** dd/mm/yyyy HH:mm — e.g. 19/08/2026 16:00. 24-hour, so it never depends on a locale's am/pm. */
export function formatDateTime(value, fallback = "—") {
  const d = parse(value);
  if (!d) return fallback;
  return `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "UTC+5:30" for the viewer's own timezone — e.g. to sit next to a local time
 * that is being compared against something stamped in UTC (an emailed report,
 * an API timestamp). Minutes are only shown when they are non-zero, so most
 * readers see the plain "UTC+5" / "UTC-8" they actually think in.
 */
export function localUtcOffset() {
  const mins = -new Date().getTimezoneOffset(); // JS's sign is backwards from UTC+N
  const sign = mins < 0 ? "-" : "+";
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return `UTC${sign}${h}${m ? ":" + pad(m) : ""}`;
}

// ---------------------------------------------------------------------------
// UTC renderings, for the values the BACKEND owns and stamps in UTC.
//
// Everything above renders in the viewer's own timezone, which is right for "the
// day this happened". It is wrong for a value the reader is going to compare
// against an email: a usage report stamps "Generated 11 Sep 2026, 05:47 UTC" in
// the recipient's inbox, and the page that scheduled it showed the same instant
// as "11/09/2026 11:17" — two clock faces for one moment, with nothing saying
// why, which reads as a wrong time rather than as a timezone.
//
// The product has no per-user or per-company timezone (no such column, no
// ZoneInfo anywhere in the backend); every stored timestamp is UTC and the
// scheduler runs on UTC. So report timestamps are shown in UTC and LABELLED,
// and the string produced here is character-for-character the one
// reporting/usage_report._fmt_dt() puts in the email.
const UTC_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "11 Sep 2026" in UTC. */
export function formatUtcDate(value, fallback = "—") {
  const d = parse(value);
  if (!d) return fallback;
  return `${pad(d.getUTCDate())} ${UTC_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "11 Sep 2026, 05:47 UTC" — the same string the emailed report carries. */
export function formatUtcDateTime(value, fallback = "—") {
  const d = parse(value);
  if (!d) return fallback;
  return `${formatUtcDate(d)}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

/** A UTC instant rendered in a named IANA zone, e.g. "11 Sep 2026, 06:14 PM IST".
 *
 * Companion to formatUtcDateTime, NOT a replacement: that one is used elsewhere
 * and keeps rendering UTC. This is for the Usage Report, where the page has to
 * agree with the timestamp the emailed report carries — the backend renders that
 * with reporting.usage_report._fmt_dt and serves both the zone and its label.
 *
 * `label` comes from the API (display_timezone_label) rather than from Intl on
 * purpose: the browser renders Asia/Kolkata as "GMT+5:30" under en-GB, so a page
 * labelling it locally would disagree with an email that says "IST" about the
 * very same zone. The month name is built from a fixed table for the same
 * reason — ICU renders September as "Sept" in en-GB and "Sep" in en-US.
 *
 * A zone the browser cannot resolve falls back to the UTC rendering rather than
 * throwing, so a bad value degrades instead of blanking the page.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function formatZonedDateTime(value, timeZone, label, fallback = "—") {
  const d = parse(value);
  if (!d) return fallback;
  if (!timeZone || timeZone.toUpperCase() === "UTC") return formatUtcDateTime(d, fallback);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone, day: "2-digit", month: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
    }).formatToParts(d);
    const get = (t) => (parts.find((p) => p.type === t) || {}).value || "";
    const month = MONTHS[Number(get("month")) - 1] || get("month");
    const zone = label || timeZone;
    return `${get("day")} ${month} ${get("year")}, ${get("hour")}:${get("minute")} ${get("dayPeriod").toUpperCase()} ${zone}`;
  } catch {
    return formatUtcDateTime(d, fallback);
  }
}
